import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone, Plus, Edit, Trash2, RefreshCw, Shield,
  CheckCircle, XCircle, AlertTriangle, Copy,
  Settings, Clock, Wifi, Loader2, Zap, Link2,
  PhoneCall, Radio, Activity, Globe, ArrowRight, ChevronDown, KeyRound, Wrench
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useAuth, API } from '../../App';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import ChannelCalendarCard from '../../components/ChannelCalendarCard';
import DemoModeToggleCard from '../../components/DemoModeToggleCard';
import ManualWhatsAppCredentialsDialog from '../../components/ManualWhatsAppCredentialsDialog';

// ==================== SUB-COMPONENTS ====================

const StatusDot = ({ status }) => {
  const colors = {
    connected: 'bg-emerald-500',
    needs_attention: 'bg-amber-500',
    disconnected: 'bg-red-500',
    no_connection: 'bg-gray-400',
    unknown: 'bg-gray-400',
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || colors.unknown}`} />;
};

const WabaStatusBadge = ({ status }) => {
  const map = {
    connected: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Connected' },
    needs_attention: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Needs Attention' },
    disconnected: { cls: 'bg-red-50 text-red-700 border-red-200', label: 'Disconnected' },
  };
  const s = map[status] || { cls: 'bg-gray-50 text-gray-600 border-gray-200', label: status || 'Unknown' };
  return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
};

// ==================== MAIN COMPONENT ====================

const ChannelsPage = () => {
  const { token, user } = useAuth();
  const location = useLocation();

  // Data
  const [channels, setChannels] = useState([]);
  const [limits, setLimits] = useState(null);
  const [users, setUsers] = useState([]);
  const [wabas, setWabas] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [numbersGrouped, setNumbersGrouped] = useState({});
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [addChannelOpen, setAddChannelOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [healthPanelOpen, setHealthPanelOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Add Channel form
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [channelName, setChannelName] = useState('');
  const [assignedAdminId, setAssignedAdminId] = useState('');
  const [creatingChannel, setCreatingChannel] = useState(false);

  // Edit form
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Embedded Signup
  const [embeddedSignupLoading, setEmbeddedSignupLoading] = useState(false);
  const [manualCredsOpen, setManualCredsOpen] = useState(false);
  const [manualCredsChannelId, setManualCredsChannelId] = useState(null);
  // ADDITIVE (Feb 2026): when opened from the top "Manual Setup" button we
  // allow creating a brand-new channel. Per-row / broken-banner triggers stay
  // strictly in update-mode (legacy behavior).
  const [manualCredsAllowCreate, setManualCredsAllowCreate] = useState(false);
  const embeddedSignupStateRef = useRef(null);
  const [appConfig, setAppConfig] = useState(null);

  // Sync
  const [syncing, setSyncing] = useState(false);

  const isOwner = user?.role === 'owner' || user?.is_super_admin;
  const orgId = user?.organization_id;

  // ==================== DATA FETCHERS ====================

  const fetchAll = useCallback(async () => {
    if (!token || !orgId) return;
    setLoading(true);
    try {
      await _doFetch();
    } finally {
      setLoading(false);
    }
  }, [token, orgId]);

  // Silent refresh — does NOT show the full-page loading spinner
  const refreshSilent = useCallback(async () => {
    if (!token || !orgId) return;
    setRefreshing(true);
    try {
      await _doFetch();
    } finally {
      setRefreshing(false);
    }
  }, [token, orgId]);

  // Shared data-fetch logic (no loading state management)
  const _doFetch = useCallback(async () => {
    try {
      const [channelsRes, limitsRes, usersRes, wabasRes, numbersRes, healthRes] = await Promise.all([
        axios.get(`${API}/channels`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { channels: [] } })),
        axios.get(`${API}/channels/limits/check`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/org/${orgId}/whatsapp/wabas`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { wabas: [] } })),
        axios.get(`${API}/org/${orgId}/whatsapp/phone-numbers`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { numbers: [], grouped_by_waba: {} } })),
        axios.get(`${API}/org/${orgId}/whatsapp/health`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);

      setChannels(channelsRes.data.channels || []);
      setLimits(limitsRes.data);
      setUsers((usersRes.data || []).filter(u => u.role === 'admin'));
      setWabas(wabasRes.data.wabas || []);
      setPhoneNumbers(numbersRes.data.numbers || []);
      setNumbersGrouped(numbersRes.data.grouped_by_waba || {});
      setHealth(healthRes.data);
    } catch (err) {
      console.error('[ChannelsPage] fetch error:', err);
    }
  }, [token, orgId]);

  // Poll until WABAs appear (handles async backend processing delay)
  const pollUntilWabasAppear = useCallback(async (maxAttempts = 4, intervalMs = 2500) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, intervalMs));
      try {
        const res = await axios.get(`${API}/org/${orgId}/whatsapp/wabas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const wabaList = res.data?.wabas || [];
        if (wabaList.length > 0) {
          // WABAs found — do a full silent refresh to populate all state
          await refreshSilent();
          return true;
        }
      } catch {
        // ignore polling errors
      }
    }
    // Final silent refresh even if polling didn't find WABAs
    await refreshSilent();
    return false;
  }, [token, orgId, refreshSilent]);

  useEffect(() => {
    fetchAll();
    axios.get(`${API}/config`)
      .then(res => setAppConfig(res.data))
      .catch(err => console.error('[CONFIG] Failed to load runtime config', err));
  }, [fetchAll]);

  // ==================== META EMBEDDED SIGNUP ====================

  useEffect(() => {
    if (!appConfig?.meta_app_id) return;
    function initFB() {
      if (!window.FB) return;
      try {
        window.FB.init({ appId: appConfig.meta_app_id, cookie: true, xfbml: false, version: 'v18.0' });
        window.fbReady = true;
      } catch (err) {
        console.error('[FB SDK] Init error:', err);
      }
    }
    if (window.FB) { initFB(); return; }
    window.fbReady = false;
    window.fbAsyncInit = function () { initFB(); };
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, [appConfig]);

  useEffect(() => {
    const handleMetaMessage = async (event) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      const payload = event.data;
      console.log("🔥 FULL META EVENT:", event);
      console.log("🔥 RAW PAYLOAD:", payload);
      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (payload.event === 'FINISH') {
        let data = payload.data;

        // Handle string payload (Meta sometimes sends stringified JSON)
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.error("[EMBEDDED_SIGNUP] Failed to parse payload.data", e);
            data = {};
          }
        }

        // Handle alternate payload shapes
        const code = data?.code || data?.auth_code || null;
        const waba_id = data?.waba_id || data?.wabaId || data?.whatsapp_business_account_id || null;
        const phone_number_id = data?.phone_number_id || data?.phoneNumberId || null;

        if (!code) {
          toast.error('WhatsApp signup incomplete. No authorization code received.');
          setEmbeddedSignupLoading(false);
          return;
        }
        console.log('[EMBEDDED_SIGNUP] WA_EMBEDDED_SIGNUP FINISH received with code');
        console.log(`[EMBEDDED_SIGNUP] FINISH payload: code=${code ? 'present' : 'MISSING'} waba_id=${waba_id || 'MISSING'} phone_number_id=${phone_number_id || 'MISSING'}`);
        if (!waba_id) {
          console.error('[EMBEDDED_SIGNUP] CRITICAL: waba_id NOT in FINISH payload — frontend extraction issue');
        }
        // Use shared handler — it checks and clears embeddedSignupStateRef atomically
        await _processSignupCode(code, waba_id || null, phone_number_id || null);
      } else if (payload.event === 'CANCEL') {
        toast.info('WhatsApp setup was cancelled.');
        setEmbeddedSignupLoading(false);
        embeddedSignupStateRef.current = null;
      } else if (payload.event === 'ERROR') {
        toast.error('WhatsApp signup encountered an error.');
        setEmbeddedSignupLoading(false);
        embeddedSignupStateRef.current = null;
      }
    };
    window.addEventListener('message', handleMetaMessage);
    return () => window.removeEventListener('message', handleMetaMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshSilent, pollUntilWabasAppear, wabas.length]);

  // Handle redirect result from GET callback (query params)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    const error = params.get('error');
    const signupResult = params.get('embedded_signup');

    if (status === 'connected' || signupResult === 'success') {
      toast.success('WhatsApp connected successfully!');
      setEmbeddedSignupLoading(false);
      embeddedSignupStateRef.current = null;
      refreshSilent().then(() => pollUntilWabasAppear());
      window.history.replaceState({}, '', location.pathname);
    } else if (error) {
      toast.error(`WhatsApp signup failed: ${decodeURIComponent(error.replace(/\+/g, ' '))}`);
      setEmbeddedSignupLoading(false);
      embeddedSignupStateRef.current = null;
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.search, refreshSilent, pollUntilWabasAppear]);

  const handleEmbeddedSignup = async () => {
    setEmbeddedSignupLoading(true);

    try {
      const response = await axios.post(`${API}/meta/embedded-signup/initiate`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { state: oauthState, oauth_url: oauthUrl } = response.data;
      embeddedSignupStateRef.current = oauthState;

      // Prefer FB SDK — hardcode config_id for Embedded Signup (returns WABA selection)
      if (window.fbReady && window.FB) {
        const configId = appConfig?.meta_embedded_signup_config_id;

        window.FB.login(
          function (loginResponse) {
            // PATH A: FB.login callback fires FIRST but does NOT contain waba_id.
            // The WA_EMBEDDED_SIGNUP FINISH message (PATH B) is the authoritative
            // source of {code, waba_id, phone_number_id}. Do NOT process here.
            if (loginResponse?.authResponse?.code) {
              console.log('[EMBEDDED_SIGNUP] FB.login returned code — deferring to WA_EMBEDDED_SIGNUP FINISH for waba_id');
              // Do NOT call _processSignupCode here. PATH B handles it.
              return;
            }

            if (loginResponse?.status === 'connected' && loginResponse?.authResponse) {
              console.log('[EMBEDDED_SIGNUP] FB.login connected — deferring to WA_EMBEDDED_SIGNUP FINISH for waba_id');
              return;
            }

            // User closed popup without completing — WA_EMBEDDED_SIGNUP
            // message may still arrive. Wait before declaring cancelled.
            if (loginResponse?.status === 'unknown' || !loginResponse?.authResponse) {
              setTimeout(() => {
                if (embeddedSignupStateRef.current) {
                  // State still set = no FINISH message arrived = user truly cancelled
                  console.log('[EMBEDDED_SIGNUP] Timeout: no callback received, declaring cancelled');
                  setEmbeddedSignupLoading(false);
                  embeddedSignupStateRef.current = null;
                  toast.info('WhatsApp setup was cancelled or timed out.');
                }
              }, 15000);
            }
          },
          { config_id: configId, response_type: 'code', override_default_response_type: true }
        );
      } else {
        // PATH C: Direct OAuth redirect (no FB SDK)
        console.log('[EMBEDDED_SIGNUP] FB SDK not available, using direct OAuth redirect');
        window.open(oauthUrl, '_blank', 'width=700,height=800,scrollbars=yes');
        // The GET callback /api/meta/embedded-signup/callback will handle
        // the redirect and process_embedded_signup runs server-side.
        // Set a timeout to clear loading state if user closes the window.
        setTimeout(() => {
          if (embeddedSignupStateRef.current) {
            setEmbeddedSignupLoading(false);
            embeddedSignupStateRef.current = null;
          }
        }, 120000); // 2 min timeout for redirect flow
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate WhatsApp signup');
      setEmbeddedSignupLoading(false);
      embeddedSignupStateRef.current = null;
    }
  };

  // Shared handler: process a signup code from any path (FB.login callback or WA_EMBEDDED_SIGNUP event)
  const _processSignupCode = async (code, wabaId, phoneNumberId) => {
    const state = embeddedSignupStateRef.current;
    if (!state) {
      console.warn('[EMBEDDED_SIGNUP] No state ref when processing code — already handled or expired');
      setEmbeddedSignupLoading(false);
      return;
    }
    // Clear ref immediately to prevent double-processing
    embeddedSignupStateRef.current = null;

    try {
      console.log(`[EMBEDDED_SIGNUP] Calling backend /complete | waba_id=${wabaId || 'MISSING'} phone_number_id=${phoneNumberId || 'MISSING'} code=${code ? 'present' : 'MISSING'}`);
      const res = await axios.post(`${API}/meta/embedded-signup/complete`,
        { code, state, waba_id: wabaId || undefined, phone_number_id: phoneNumberId || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        const { wabas_connected = 0, numbers_synced = 0 } = res.data;
        toast.success(`WhatsApp connected! ${wabas_connected} WABA(s), ${numbers_synced} number(s) synced.`);
        await refreshSilent();
        // Poll if WABAs not yet visible (async processing delay)
        if (wabas.length === 0) {
          await pollUntilWabasAppear();
        }
      } else {
        toast.error(res.data?.error || 'Connection partially completed.');
        await refreshSilent();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'WhatsApp signup failed.';
      toast.error(detail);
      console.error('[EMBEDDED_SIGNUP] Callback error:', err.response?.data || err.message);
    } finally {
      setEmbeddedSignupLoading(false);
    }
  };

  // ==================== SYNC ====================

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/org/${orgId}/whatsapp/sync`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(`Synced ${res.data.total_synced} phone number(s) from ${res.data.wabas_processed} WABA(s).`);
      } else if (res.data.errors?.length) {
        toast.warning(`Sync completed with errors: ${res.data.errors[0]}`);
      } else {
        toast.info(res.data.error || 'No WABAs to sync.');
      }
      refreshSilent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  // ==================== ADD CHANNEL ====================

  const handleAddChannel = async () => {
    if (!selectedPhoneNumber || !channelName.trim()) {
      toast.error('Please select a phone number and enter a channel name.');
      return;
    }
    setCreatingChannel(true);
    try {
      const res = await axios.post(`${API}/channels/create`,
        {
          phone_number_id: selectedPhoneNumber.phone_number_id,
          display_name: channelName.trim(),
          assigned_admin_id: assignedAdminId || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success('Channel created successfully!');
        setAddChannelOpen(false);
        resetAddChannelForm();
        refreshSilent();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create channel.');
    } finally {
      setCreatingChannel(false);
    }
  };

  const resetAddChannelForm = () => {
    setSelectedPhoneNumber(null);
    setChannelName('');
    setAssignedAdminId('');
  };

  // ==================== EDIT CHANNEL ====================

  const openEditDialog = (channel) => {
    setSelectedChannel(channel);
    setEditFormData({
      display_name: channel.display_name || '',
      assigned_admin_id: channel.assigned_admin_id || '',
      escalation_holding_message: channel.escalation_holding_message || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateChannel = async () => {
    if (!selectedChannel) return;
    setSaving(true);
    try {
      await axios.put(`${API}/channels/${selectedChannel.id}`, editFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Channel updated.');
      setEditDialogOpen(false);
      setSelectedChannel(null);
      refreshSilent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  // ==================== DELETE CHANNEL ====================

  const handleDeleteChannel = async (channelId) => {
    if (!confirm('Deactivate this channel? This will unassign the phone number.')) return;
    try {
      await axios.delete(`${API}/channels/${channelId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Channel deactivated.');
      refreshSilent();
    } catch (err) {
      toast.error('Failed to deactivate channel.');
    }
  };

  // ==================== HEALTH CHECK ====================

  const triggerHealthCheck = async (channelId) => {
    try {
      await axios.post(`${API}/channels/${channelId}/health-check`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Health check triggered.');
      refreshSilent();
    } catch (err) {
      toast.error('Health check failed.');
    }
  };

  // ==================== HELPERS ====================

  const getAdminName = (adminId) => {
    const admin = users.find(u => u.id === adminId);
    return admin ? admin.name || admin.email : 'Unassigned';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const availableNumbers = phoneNumbers.filter(n => !n.is_assigned);
  const hasWabas = wabas.length > 0;
  const hasNumbers = phoneNumbers.length > 0;
  const allUsed = hasNumbers && availableNumbers.length === 0;

  // ==================== LOADING STATE ====================

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="channels-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // ==================== RENDER ====================

  return (
    <div className="space-y-6" data-testid="channels-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            WhatsApp Channels
            {refreshing && <Loader2 className="inline-block h-4 w-4 ml-2 animate-spin text-gray-400" />}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your WhatsApp Business connections and channels
          </p>
        </div>
        <div className="flex items-center gap-2">
          {limits && (
            <Badge variant="outline" className="mr-1" data-testid="channel-limit-badge">
              {limits.current}/{limits.limit} channels
            </Badge>
          )}
          {isOwner && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSync}
                      disabled={syncing || !hasWabas}
                      data-testid="sync-numbers-btn"
                    >
                      {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Refresh numbers from Meta</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Button
                variant="outline"
                onClick={handleEmbeddedSignup}
                disabled={embeddedSignupLoading || !window.fbReady || !appConfig}
                data-testid="connect-whatsapp-btn"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                {embeddedSignupLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
                Connect WhatsApp
              </Button>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setManualCredsChannelId(null);
                        setManualCredsAllowCreate(true);
                        setManualCredsOpen(true);
                      }}
                      data-testid="manual-setup-btn"
                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      <KeyRound className="h-4 w-4 mr-2" />
                      Manual Setup
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Use if Embedded Signup is unavailable, or to replace an expired Meta token.</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Button
                onClick={() => { resetAddChannelForm(); setAddChannelOpen(true); }}
                disabled={(limits && !limits.allowed) || !hasNumbers || allUsed}
                data-testid="add-channel-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Channel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Demo Mode inline toggle — secondary access point */}
      <DemoModeToggleCard variant="inline" />

      {/* Per-channel Booking & Calendar control center */}
      {channels.length > 0 && (
        <Card data-testid="per-channel-calendar-section">
          <CardHeader>
            <CardTitle>Booking &amp; Calendar — per channel</CardTitle>
            <CardDescription>
              This channel controls how bookings behave. Configure the Booking Mode,
              connect a Google Calendar (or run in Demo), and set slot rules per channel —
              no settings are shared across channels.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {channels.map(ch => <ChannelCalendarCard key={ch.id} channel={ch} />)}
          </CardContent>
        </Card>
      )}

      {/* ===== STATE: No WABA ===== */}
      {!hasWabas && (
        <Card className="border-dashed border-2 border-gray-200" data-testid="no-waba-card">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
              <Phone className="h-8 w-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Connect Your WhatsApp Business Account</h3>
            <p className="text-sm text-muted-foreground max-w-md mb-6">
              Get started by connecting your WhatsApp Business Account (WABA) through Meta's Embedded Signup.
              This will import your phone numbers so you can create channels.
            </p>
            {isOwner && (
              <Button
                size="lg"
                onClick={handleEmbeddedSignup}
                disabled={embeddedSignupLoading || !window.fbReady || !appConfig}
                data-testid="connect-whatsapp-cta-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {embeddedSignupLoading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Zap className="h-5 w-5 mr-2" />}
                Connect WhatsApp
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== WABA + Health Overview ===== */}
      {hasWabas && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="waba-overview">
          {/* Health Summary Card */}
          <Card className="border-l-4 border-l-emerald-500" data-testid="health-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Activity className="h-4 w-4" /> Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-2">
                <StatusDot status={health?.overall_status || 'unknown'} />
                <span className="font-semibold capitalize">
                  {(health?.overall_status || 'unknown').replace('_', ' ')}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs p-0 h-auto"
                onClick={() => setHealthPanelOpen(true)}
                data-testid="view-health-details-btn"
              >
                View details <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </CardContent>
          </Card>

          {/* Numbers Card */}
          <Card className="border-l-4 border-l-blue-500" data-testid="numbers-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <PhoneCall className="h-4 w-4" /> Phone Numbers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{health?.total_numbers || phoneNumbers.length}</div>
              <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                <span>{health?.assigned_numbers ?? 0} assigned</span>
                <span>{health?.available_numbers ?? availableNumbers.length} available</span>
              </div>
            </CardContent>
          </Card>

          {/* WABAs Card */}
          <Card className="border-l-4 border-l-violet-500" data-testid="wabas-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Globe className="h-4 w-4" /> WABA Connections
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{wabas.length}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {wabas.map(w => (
                  <WabaStatusBadge key={w.id} status={w.connection_status || 'unknown'} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== STATE: WABA exists but no numbers ===== */}
      {hasWabas && !hasNumbers && (
        <Alert data-testid="no-numbers-alert">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Your WABA is connected but no phone numbers were found. Add numbers in{' '}
            <a
              href="https://business.facebook.com/wa/manage/phone-numbers/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-blue-600"
            >
              Meta Business Manager
            </a>,
            then click <strong>Refresh</strong> to sync.
          </AlertDescription>
        </Alert>
      )}

      {/* ===== STATE: All numbers used ===== */}
      {hasWabas && allUsed && (
        <Alert data-testid="all-numbers-used-alert">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription>
            All phone numbers are assigned to channels. Connect another WABA or add more numbers in Meta Business Manager to create new channels.
          </AlertDescription>
        </Alert>
      )}

      {/* ===== WABA Details (expandable) ===== */}
      {hasWabas && (
        <Card data-testid="waba-details-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              WABA Connections
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>WABA ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Numbers</TableHead>
                  <TableHead>Last Synced</TableHead>
                  <TableHead>Token</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wabas.map(waba => {
                  const wabaNumbers = numbersGrouped[waba.waba_id] || [];
                  return (
                    <TableRow key={waba.id} data-testid={`waba-row-${waba.waba_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{waba.waba_id}</code>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(waba.waba_id)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <WabaStatusBadge status={waba.connection_status || 'unknown'} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {wabaNumbers.length} total
                          ({wabaNumbers.filter(n => !n.is_assigned).length} available)
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {waba.last_sync_at ? new Date(waba.last_sync_at).toLocaleString() : 'Never'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {waba.access_token_valid === true && <CheckCircle className="h-4 w-4 text-emerald-500" />}
                          {waba.access_token_valid === false && <XCircle className="h-4 w-4 text-red-500" />}
                          {waba.access_token_valid == null && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                          <span className="text-xs text-muted-foreground">
                            {waba.access_token_masked || 'Encrypted'}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ===== BROKEN CONNECTION BANNER ===== */}
      {channels.length > 0 && (() => {
        const broken = channels.filter(c =>
          c.status === 'token_expired' ||
          c.token_status === 'expired' ||
          c.token_status === 'invalid' ||
          c.last_health_status === 'invalid' ||
          c.last_health_status === 'error' ||
          c.last_health_status === 'token_expired' ||
          c.credentials_verified === false
        );
        if (broken.length === 0) return null;
        return (
          <Alert
            className="border-amber-300 bg-amber-50 text-amber-900"
            data-testid="broken-connection-banner"
          >
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-sm">
                <strong>
                  {broken.length} channel{broken.length === 1 ? '' : 's'} need{broken.length === 1 ? 's' : ''} attention.
                </strong>{' '}
                WhatsApp token has expired or the connection is unhealthy — outbound messages won't send until you reconnect.
              </span>
              <Button
                size="sm"
                onClick={() => {
                  const first = broken[0];
                  setManualCredsChannelId(first.id);
                  setManualCredsAllowCreate(false);
                  setManualCredsOpen(true);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white border-0"
                data-testid="broken-banner-reconnect-btn"
              >
                <Wrench className="h-4 w-4 mr-1" />
                Reconnect now
              </Button>
            </AlertDescription>
          </Alert>
        );
      })()}

      {/* ===== CHANNELS TABLE ===== */}
      {channels.length > 0 && (
        <Card data-testid="channels-table-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Radio className="h-4 w-4" />
              Active Channels
            </CardTitle>
            <CardDescription>
              {channels.length} channel{channels.length !== 1 ? 's' : ''} active
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned Admin</TableHead>
                  <TableHead>Health</TableHead>
                  {isOwner && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {channels.map(channel => {
                  const tokenInvalid =
                    channel.token_status === 'expired' ||
                    channel.token_status === 'invalid' ||
                    (channel.token_status && channel.token_status !== 'valid');
                  const healthBad =
                    channel.last_health_status === 'invalid' ||
                    channel.last_health_status === 'error' ||
                    channel.last_health_status === 'token_expired';
                  const needsAttn =
                    channel.status === 'token_expired' ||
                    tokenInvalid ||
                    healthBad ||
                    channel.credentials_verified === false;
                  return (
                  <TableRow
                    key={channel.id}
                    data-testid={`channel-row-${channel.id}`}
                    className={needsAttn ? 'bg-amber-50/40' : undefined}
                  >
                    <TableCell>
                      <div>
                        <span className="font-medium">{channel.display_name}</span>
                        {needsAttn && (
                          <Badge
                            variant="outline"
                            className="ml-2 bg-amber-100 text-amber-800 border-amber-300 text-[10px]"
                            data-testid={`channel-action-required-${channel.id}`}
                          >
                            Action required
                          </Badge>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5">
                          ID: {channel.phone_number_id ? channel.phone_number_id.slice(0, 12) + '...' : 'N/A'}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{channel.display_phone_number || 'N/A'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          channel.status === 'connected' || channel.status === 'active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : channel.status === 'disconnected' || channel.status === 'error'
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                        }
                        data-testid={`channel-status-${channel.id}`}
                      >
                        {channel.status || 'unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{getAdminName(channel.assigned_admin_id)}</span>
                    </TableCell>
                    <TableCell>
                      <ChannelHealthBadge channel={channel} onCheck={() => triggerHealthCheck(channel.id)} />
                    </TableCell>
                    {isOwner && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={needsAttn ? 'default' : 'ghost'}
                                  size="sm"
                                  onClick={() => { setManualCredsChannelId(channel.id); setManualCredsAllowCreate(false); setManualCredsOpen(true); }}
                                  className={needsAttn ? 'bg-amber-500 hover:bg-amber-600 text-white border-0 shadow-sm ring-2 ring-amber-300 ring-offset-1' : ''}
                                  data-testid={`reconnect-whatsapp-${channel.id}`}
                                >
                                  <Wrench className="h-4 w-4 mr-1" />
                                  Reconnect WhatsApp
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {needsAttn
                                  ? 'Token expired or connection unhealthy — paste new Meta credentials to restore sending'
                                  : 'Replace Meta credentials manually (fallback to Embedded Signup)'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(channel)}
                                  data-testid={`edit-channel-${channel.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteChannel(channel.id)}
                                  data-testid={`delete-channel-${channel.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Deactivate</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* No channels but WABA exists */}
      {channels.length === 0 && hasWabas && hasNumbers && !allUsed && (
        <Card className="border-dashed" data-testid="no-channels-card">
          <CardContent className="flex flex-col items-center py-12 text-center">
            <Radio className="h-10 w-10 text-gray-300 mb-3" />
            <h3 className="font-semibold mb-1">No Channels Yet</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              You have {availableNumbers.length} available phone number{availableNumbers.length !== 1 ? 's' : ''}.
              Create a channel to start receiving messages.
            </p>
            {isOwner && (
              <Button onClick={() => { resetAddChannelForm(); setAddChannelOpen(true); }} data-testid="create-first-channel-btn">
                <Plus className="h-4 w-4 mr-2" /> Create First Channel
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== ADD CHANNEL MODAL ===== */}
      <Dialog open={addChannelOpen} onOpenChange={setAddChannelOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="add-channel-dialog">
          <DialogHeader>
            <DialogTitle>Add Channel</DialogTitle>
            <DialogDescription>Select an available phone number to create a new channel.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Step 1: Select number */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Select Phone Number</Label>
              {Object.keys(numbersGrouped).length > 0 ? (
                <div className="space-y-3 max-h-60 overflow-y-auto border rounded-md p-3" data-testid="phone-number-list">
                  {Object.entries(numbersGrouped).map(([wabaId, nums]) => (
                    <div key={wabaId}>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
                        <Globe className="h-3 w-3" /> WABA: {wabaId}
                      </p>
                      <div className="space-y-1">
                        {nums.map(num => {
                          const isUsed = num.is_assigned;
                          const isSelected = selectedPhoneNumber?.phone_number_id === num.phone_number_id;
                          return (
                            <button
                              key={num.phone_number_id}
                              type="button"
                              disabled={isUsed}
                              onClick={() => {
                                setSelectedPhoneNumber(num);
                                if (!channelName) {
                                  setChannelName(num.verified_name || num.display_phone_number || `Channel ${num.phone_number_id.slice(-4)}`);
                                }
                              }}
                              className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between ${
                                isUsed
                                  ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                                  : isSelected
                                  ? 'bg-emerald-50 border border-emerald-300 text-emerald-800'
                                  : 'bg-white border border-gray-200 hover:border-emerald-200 hover:bg-emerald-50/50 cursor-pointer'
                              }`}
                              data-testid={`phone-number-option-${num.phone_number_id}`}
                            >
                              <div className="flex items-center gap-2">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{num.display_phone_number || num.phone_number_id}</span>
                                {num.verified_name && (
                                  <span className="text-xs text-muted-foreground">({num.verified_name})</span>
                                )}
                              </div>
                              <div>
                                {isUsed ? (
                                  <Badge variant="outline" className="text-xs bg-gray-100">In Use</Badge>
                                ) : isSelected ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-600 border-emerald-200">Available</Badge>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No phone numbers available. Connect a WABA first.</p>
              )}
            </div>

            {/* Step 2: Channel Name */}
            {selectedPhoneNumber && (
              <>
                <div>
                  <Label htmlFor="channel-name">Channel Name</Label>
                  <Input
                    id="channel-name"
                    value={channelName}
                    onChange={e => setChannelName(e.target.value)}
                    placeholder="e.g., Sales, Support"
                    data-testid="channel-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="assigned-admin">Assigned Admin (optional)</Label>
                  <Select value={assignedAdminId} onValueChange={setAssignedAdminId}>
                    <SelectTrigger id="assigned-admin" data-testid="assigned-admin-select">
                      <SelectValue placeholder="No admin assigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No admin assigned</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddChannelOpen(false)} data-testid="cancel-add-channel">
              Cancel
            </Button>
            <Button
              onClick={handleAddChannel}
              disabled={!selectedPhoneNumber || !channelName.trim() || creatingChannel}
              data-testid="confirm-add-channel"
            >
              {creatingChannel ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== EDIT CHANNEL MODAL ===== */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-channel-dialog">
          <DialogHeader>
            <DialogTitle>Edit Channel</DialogTitle>
            <DialogDescription>Update channel settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-name">Channel Name</Label>
              <Input
                id="edit-name"
                value={editFormData.display_name || ''}
                onChange={e => setEditFormData(p => ({ ...p, display_name: e.target.value }))}
                data-testid="edit-channel-name"
              />
            </div>
            <div>
              <Label htmlFor="edit-admin">Assigned Admin</Label>
              <Select
                value={editFormData.assigned_admin_id || 'none'}
                onValueChange={v => setEditFormData(p => ({ ...p, assigned_admin_id: v === 'none' ? null : v }))}
              >
                <SelectTrigger id="edit-admin" data-testid="edit-assigned-admin">
                  <SelectValue placeholder="No admin assigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No admin assigned</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-holding-msg">Escalation Holding Message</Label>
              <Input
                id="edit-holding-msg"
                value={editFormData.escalation_holding_message || ''}
                onChange={e => setEditFormData(p => ({ ...p, escalation_holding_message: e.target.value }))}
                placeholder="Message sent when escalating to human agent"
                data-testid="edit-holding-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateChannel} disabled={saving} data-testid="save-edit-channel">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== HEALTH DETAILS PANEL ===== */}
      <Dialog open={healthPanelOpen} onOpenChange={setHealthPanelOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="health-panel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" /> WhatsApp Health
            </DialogTitle>
            <DialogDescription>Overall connection health and per-WABA status</DialogDescription>
          </DialogHeader>
          {health ? (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <StatusDot status={health.overall_status} />
                <span className="font-semibold capitalize text-lg">
                  {health.overall_status.replace('_', ' ')}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="border rounded-md p-3">
                  <div className="text-xl font-bold">{health.waba_count}</div>
                  <div className="text-xs text-muted-foreground">WABAs</div>
                </div>
                <div className="border rounded-md p-3">
                  <div className="text-xl font-bold">{health.assigned_numbers}</div>
                  <div className="text-xs text-muted-foreground">Assigned</div>
                </div>
                <div className="border rounded-md p-3">
                  <div className="text-xl font-bold">{health.available_numbers}</div>
                  <div className="text-xs text-muted-foreground">Available</div>
                </div>
              </div>
              {health.wabas?.map(w => (
                <div key={w.waba_id} className="border rounded-md p-3" data-testid={`health-waba-${w.waba_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{w.waba_id}</code>
                    <WabaStatusBadge status={w.connection_status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <div>Numbers: {w.phone_numbers_count}</div>
                    <div>Assigned: {w.assigned_count}</div>
                    <div>Available: {w.available_count}</div>
                    <div>Token: {w.access_token_valid ? 'Valid' : w.access_token_valid === false ? 'Invalid' : 'Unknown'}</div>
                    <div>Last Sync: {w.last_sync_at ? new Date(w.last_sync_at).toLocaleString() : 'Never'}</div>
                    <div>Last Check: {w.last_token_check ? new Date(w.last_token_check).toLocaleString() : 'Never'}</div>
                  </div>
                </div>
              ))}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="health-sync-btn">
                  {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Refresh Numbers
                </Button>
                <Button variant="outline" size="sm" onClick={handleEmbeddedSignup} disabled={embeddedSignupLoading} data-testid="health-reconnect-btn">
                  <Zap className="h-4 w-4 mr-1" /> Reconnect
                </Button>
              </div>
            </div>
          ) : (
            <p className="py-4 text-center text-muted-foreground">No health data available.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== MANUAL WHATSAPP CREDENTIALS DIALOG (permanent fallback) ===== */}
      <ManualWhatsAppCredentialsDialog
        open={manualCredsOpen}
        onOpenChange={(v) => {
          setManualCredsOpen(v);
          if (!v) setManualCredsAllowCreate(false);
        }}
        channels={channels}
        preselectedChannelId={manualCredsChannelId}
        allowCreate={manualCredsAllowCreate}
        defaultMode={
          manualCredsAllowCreate && (channels?.length ?? 0) === 0
            ? 'create'
            : 'update'
        }
        limits={
          limits
            ? {
                allowed: !!limits.allowed,
                current_count: limits.current ?? limits?.channels?.current ?? 0,
                max_allowed: limits.limit ?? limits?.channels?.max ?? null,
              }
            : null
        }
        onChannelCreated={() => {
          // Refresh data immediately so new channel + quota appear.
          _doFetch && _doFetch();
        }}
        onSaved={async (data) => {
          // Fire-and-forget immediate health re-check for instant UI feedback.
          const cid = data?.channel_id || manualCredsChannelId;
          if (cid) {
            try {
              await axios.post(
                `${API}/channels/${cid}/health-check`,
                {},
                { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true },
              );
            } catch (_) { /* non-blocking */ }
          }
          _doFetch && _doFetch();
        }}
      />
    </div>
  );
};

// ==================== CHANNEL HEALTH BADGE ====================

const ChannelHealthBadge = ({ channel, onCheck }) => {
  const status = channel.last_health_status;
  const lastCheck = channel.last_health_check;

  if (!lastCheck) {
    return (
      <Badge
        className="bg-gray-100 text-gray-600 cursor-pointer"
        onClick={onCheck}
        data-testid={`health-badge-${channel.id}`}
      >
        <Clock className="h-3 w-3 mr-1" /> Not Checked
      </Badge>
    );
  }

  const map = {
    healthy: { cls: 'bg-emerald-50 text-emerald-700', icon: <CheckCircle className="h-3 w-3 mr-1" />, label: 'Healthy' },
    invalid: { cls: 'bg-red-50 text-red-700', icon: <XCircle className="h-3 w-3 mr-1" />, label: 'Invalid' },
  };
  const s = map[status] || { cls: 'bg-amber-50 text-amber-700', icon: <AlertTriangle className="h-3 w-3 mr-1" />, label: 'Issue' };

  return (
    <Badge className={`${s.cls} cursor-pointer`} onClick={onCheck} data-testid={`health-badge-${channel.id}`}>
      {s.icon} {s.label}
    </Badge>
  );
};

export default ChannelsPage;
